import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { DEFAULT_DATE_BOX_WIDTH_PT } from '@dealstack/shared/lib/common/business/stampGeometry';
import { ptToMm } from '@dealstack/shared/lib/common/documentLayout/documentLayoutConstants';

import { DATE_NODE_NAME } from './dateNode';
import { anchorInsertPoint } from './signatureDrop';

/** Private, so the id cannot drop into an input on the page as `text/plain`. */
export const DATE_DRAG_MIME = 'application/x-dealstack-date-anchor';

export interface FoundDate {
	pos: number;
	node: ProseMirrorNode;
	templateDatePlacementId: string;
}

/** Every date anchor in the live editor state, in document (`placementIndex`) order. */
export const findDates = (doc: ProseMirrorNode): FoundDate[] => {
	const found: FoundDate[] = [];
	doc.descendants((node, pos) => {
		if (node.type.name !== DATE_NODE_NAME) {
			return true;
		}
		found.push({
			pos,
			node,
			templateDatePlacementId: String(node.attrs.templateDatePlacementId ?? ''),
		});
		return false;
	});
	return found;
};

export const placedDateIds = (doc: ProseMirrorNode): string[] =>
	findDates(doc).map((entry) => entry.templateDatePlacementId);

export const insertDateAt = (
	state: EditorState,
	pos: number,
	templateDatePlacementId: string
): Transaction | null => {
	const dateType = state.schema.nodes[DATE_NODE_NAME];
	const target = anchorInsertPoint(state.doc, state.schema, pos, DATE_NODE_NAME);
	if (dateType === undefined || target === null) {
		return null;
	}
	const transaction = state.tr.insert(
		target,
		dateType.create({ templateDatePlacementId, widthMm: ptToMm(DEFAULT_DATE_BOX_WIDTH_PT) })
	);
	return transaction.docChanged ? transaction : null;
};

/**
 * Deletes every anchor for a placement id, back to front so earlier positions stay valid; null
 * when there is none. Every match, since a document holding two for one placement cannot be saved.
 */
export const removeDateAnchor = (
	state: EditorState,
	templateDatePlacementId: string
): Transaction | null => {
	const matches = findDates(state.doc).filter(
		(entry) => entry.templateDatePlacementId === templateDatePlacementId
	);
	if (matches.length === 0) {
		return null;
	}
	const transaction = state.tr;
	for (const entry of [...matches].reverse()) {
		transaction.delete(entry.pos, entry.pos + entry.node.nodeSize);
	}
	return transaction;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A UUID, not any string: a malformed id would fail generation rather than the save. */
export const parseDatePayload = (raw: string): string | null =>
	UUID_PATTERN.test(raw) ? raw : null;

/** Inserts a dropped date anchor. Unlike `handleSignatureDrop` there is no pairing. */
export const handleDateDrop = (view: EditorView, event: DragEvent): boolean => {
	const raw = event.dataTransfer?.getData(DATE_DRAG_MIME);
	if (raw === undefined || raw === '') {
		return false;
	}
	const templateDatePlacementId = parseDatePayload(raw);
	if (templateDatePlacementId === null) {
		return false;
	}
	const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
	if (coords === null) {
		return false;
	}
	const transaction = insertDateAt(view.state, coords.pos, templateDatePlacementId);
	if (transaction === null) {
		return false;
	}
	event.preventDefault();
	view.dispatch(transaction);
	return true;
};
